/**
 * Subscription Management API
 *
 * These functions interact with the backend subscription endpoints.
 * Note: Backend endpoints must be implemented before these will work.
 */

import { getCheckoutPlanKeyForStripe } from "../pricing";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

/**
 * Cancel subscription
 * @param {boolean} cancelImmediately - If true, cancel now. If false, cancel at period end.
 * @returns {Promise<Object>} Subscription cancellation response
 */
export async function cancelSubscription(cancelImmediately = false) {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}/subscriptions/cancel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      cancelImmediately,
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to cancel subscription: ${response.status}`
    );
  }

  return await response.json();
}

/**
 * Reactivate a cancelled subscription
 * @returns {Promise<Object>} Subscription reactivation response
 */
export async function reactivateSubscription() {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}/subscriptions/reactivate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to reactivate subscription: ${response.status}`
    );
  }

  return await response.json();
}

/**
 * Get subscription status
 * @returns {Promise<Object>} Subscription status details
 */
export async function getSubscriptionStatus() {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}/subscriptions/status`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to get subscription status: ${response.status}`
    );
  }

  return await response.json();
}

/**
 * Preview proration for a plan / billing-cycle change.
 * @param {string} targetPlan - starter | pro | business | growth | scale
 * @param {"monthly"|"annual"} billingCycle
 */
export async function previewSubscriptionChange(targetPlan, billingCycle) {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}/subscriptions/preview`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      targetPlan,
      billingCycle,
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to preview change: ${response.status}`
    );
  }

  return await response.json();
}

/**
 * Change subscription plan and/or monthly vs annual
 * @param {string} targetPlanKey - Target plan key: 'starter' | 'pro' | 'business' | 'growth' | 'scale'
 * @param {"monthly"|"annual"} [billingCycle] - Omitted to keep the current interval when only changing tier
 * @returns {Promise<Object>} Subscription change response
 */
export async function changeSubscription(targetPlanKey, billingCycle) {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const body = { targetPlan: targetPlanKey };
  if (billingCycle === "monthly" || billingCycle === "annual") {
    body.billingCycle = billingCycle;
  }

  const response = await fetch(`${API_URL}/subscriptions/change`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to change subscription: ${response.status}`
    );
  }

  return await response.json();
}

/**
 * Free Gateway → paid: start Stripe Checkout on the current Firebase user (no new account).
 * Uses POST /api/stripe/create-checkout-session; webhook links subscription to same user by email/uid.
 */
export async function startSubscriptionCheckoutFromApp({
  targetPlanKey,
  billingCycle,
  email,
  clientName,
  phone,
  portfolioSize,
  landlordId,
  pricingTierGroup,
}) {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }
  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error("You must be signed in to upgrade.");
  }
  const idToken = await user.getIdToken();
  const selectedPlan = getCheckoutPlanKeyForStripe(targetPlanKey);
  const res = await fetch(`${API_URL}/stripe/create-checkout-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      email: email || user.email,
      clientName:
        clientName ||
        user.displayName ||
        (email || user.email || "customer").split("@")[0],
      phone: phone || "",
      portfolioSize: portfolioSize || "",
      billingCycle: billingCycle === "annual" ? "annual" : "monthly",
      selectedPlan,
      priceId: "",
      metadata: {
        landlordId: landlordId || user.uid,
        pricingTierGroup: pricingTierGroup || "market_2026",
        source: "subscription_modal_free_to_paid",
      },
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Could not start checkout (HTTP ${res.status})`
    );
  }
  const data = await res.json();
  if (!data?.url) {
    throw new Error("Checkout did not return a URL");
  }
  window.location.assign(data.url);
}

/**
 * Create Stripe Customer Portal session
 * @param {string} returnUrl - URL to return to after portal session
 * @returns {Promise<Object>} Portal session with URL
 */
export async function createPortalSession(returnUrl) {
  if (!API_URL) {
    throw new Error("API URL not configured");
  }

  const auth = await import("firebase/auth");
  const { getAuth } = auth;
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}/billing/create-portal-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      returnUrl:
        returnUrl || `${window.location.origin}/settings?billing=updated`,
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      errorData.error || `Failed to create portal session: ${response.status}`
    );
  }

  return await response.json();
}
